import { useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import clsx from 'clsx'
import AIGlowRing from 'components/Common/AIGlowRing'
import { fillArray } from 'lib/array/arrayUtils'
import { DAYS_IN_WEEK, MAX_MONTH_WEEK_ROWS, getRangeGlowSegments } from './calendarRangeGlowSegments'
import type { CalendarWeekStart } from 'lib/datetime/calendarUtils'

/*
 * Must be rendered as a direct child of the day grid it decorates. It lays a
 * mirror grid over the cells — one column per weekday, with row sizing and gaps
 * inherited from that grid — so segments always land exactly on the week rows
 * and cannot drift when the grid's CSS changes.
 */
const OverlayRoot = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 1;
  display: grid;
  grid-template-columns: repeat(${DAYS_IN_WEEK}, 1fr);
  grid-auto-rows: inherit;
  row-gap: inherit;
`

/* Static placement classes: one per possible week row and weekday column */
const SEGMENT_PLACEMENT_RULES = [
  ...fillArray(MAX_MONTH_WEEK_ROWS, row => `&.row-${row} { grid-row: ${row + 1}; }`),
  ...fillArray(DAYS_IN_WEEK, col => `&.col-start-${col} { grid-column-start: ${col + 1}; }`),
  ...fillArray(DAYS_IN_WEEK, col => `&.col-end-${col} { grid-column-end: ${col + 2}; }`),
].join('\n')

/*
 * Only the true ends of the range are rounded; the ring inherits these corners, so
 * edges created by a week wrap or a month boundary stay square and the band reads as
 * continuous across rows and adjacent grids.
 */
const SegmentPosition = styled.div`
  position: relative;

  ${SEGMENT_PLACEMENT_RULES}

  &.cap-start {
    border-start-start-radius: ${rem(8)};
    border-end-start-radius: ${rem(8)};
  }

  &.cap-end {
    border-start-end-radius: ${rem(8)};
    border-end-end-radius: ${rem(8)};
  }
`

interface Props {
  /** Any date within the rendered month */
  monthStart: Date
  rangeStart: Date
  rangeEnd: Date
  weekStart?: CalendarWeekStart
}

function CalendarRangeGlowOverlay(props: Props) {
  const { monthStart, rangeStart, rangeEnd, weekStart = 1 } = props

  const segments = useMemo(
    () => getRangeGlowSegments(monthStart, rangeStart, rangeEnd, weekStart),
    [monthStart, rangeStart, rangeEnd, weekStart],
  )

  if (segments.length === 0) {
    return null
  }

  return (
    <OverlayRoot aria-hidden="true">
      {segments.map(segment => (
        <SegmentPosition
          key={segment.row}
          data-testid="calendar-range-glow-segment"
          className={clsx(
            `row-${segment.row}`,
            `col-start-${segment.startCol}`,
            `col-end-${segment.endCol}`,
            { 'cap-start': segment.capStart, 'cap-end': segment.capEnd },
          )}
        >
          <AIGlowRing />
        </SegmentPosition>
      ))}
    </OverlayRoot>
  )
}

export default CalendarRangeGlowOverlay
